import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProfileParams } from '../../../../../../utils/common';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import DonutChart, { DonutChartSegment } from '../../../../../common/DonutChart/DonutChart';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import detailsStyles from './TopProducts.module.scss';
import KPITableWithPills from '../../../common/KPITableWithPills/KPITableWithPills';
import KPIAccordionTable from '../../../common/KPIAccordionTable/KPIAccordionTable';
import { sanitizeTableData } from '../../../../../../utils/expandableTableDataSanitizer';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const TopProducts: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  // Display API-provided segments when available; otherwise show nothing (0 fallback is implicit per segment values)
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments) ? summary!.segments : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t('top_products')}
      dataTestId="top_products_tile"
      loading={loading}
      className={detailsStyles.center_content}
    >
      <div className={detailsStyles.tile_content}>
        <DonutChart
          size={125}
          strokeWidth={20}
          legendGap={24}
          segments={segments}
          showLegend
          isPortfolio
        />
      </div>
    </KPITile>
  );
};

export const TopProductsDetails: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = 'top10Product', onClose }) => {
  type View = 'perfYear' | 'monthly' | 'yearly';
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('perfYear');

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] =
    useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const kpiData = useMemo(() => {
    if (detailsResponse?.body?.kpis) {
      return detailsResponse.body.kpis.find((k: any) => k.kpiCode === kpiCode);
    }
    return null;
  }, [detailsResponse, kpiCode]);

  const ytdViewData = kpiData?.detail?.ytdView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const monthlyRawTableData = kpiData?.detail?.monthlyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const monthlyTableData = useMemo(
    () => sanitizeTableData(monthlyRawTableData),
    [monthlyRawTableData]
  );

  const yearlyRawTableData = kpiData?.detail?.annuallyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const yearlyTableData = useMemo(
    () => sanitizeTableData(yearlyRawTableData),
    [yearlyRawTableData]
  );

  const viewMap: Record<View, JSX.Element> = {
    perfYear: (
      <KPITableWithPills
        data={ytdViewData}
        affiliateCode={affiliateCode}
        applyGrouping={true}
        title="categorymix.py_subtitle"
        subtitle="categorymix.sales_performance_description"
      />
    ),
    monthly: (
      <KPIAccordionTable
        data={monthlyTableData}
        affiliateCode={affiliateCode}
        kpiCode={kpiCode}
        showPills={true}
        groupedRowsSize={1}
        title={t('topproducts.monthly_subtitle')}
        subtitle={t('categorymix.sales_performance_description')}
      />
    ),
    yearly: (
      <KPIAccordionTable
        data={yearlyTableData}
        affiliateCode={affiliateCode}
        kpiCode={kpiCode}
        showPills={true}
        groupedColumnClass
        highlightColumns={[1, 3]}
        title={t('topproducts.monthly_subtitle')}
        subtitle={t('categorymix.sales_performance_description')}
      />
    ),
  };

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const aiContent = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';

  const perfYear = 'PY 25';
  const periodItems = useMemo(
    () => [
      { code: 'perfYear', label: perfYear },
      { code: 'monthly', label: t('monthly') },
      { code: 'yearly', label: t('yearly') },
    ],
    [t]
  );

  const headerContent = (
    <div className={detailsStyles.header_content}>
      <div className={detailsStyles.tile_header}>
        <div className={detailsStyles.title}>{t('topproducts.title')}</div>
        <div className={detailsStyles.desc}>{t('topproducts.description')}</div>
      </div>
      <div className={detailsStyles.chart_view_wrapper}>
        <ViewModeSwitcher
          mode={viewMode}
          onChangeMode={setViewMode}
          disabled={false}
          showSwitch={false}
          selectedTimeRangeCode={selectedPeriod}
          onSelectTimeRange={setSelectedPeriod}
          timeRanges={periodItems}
        />
      </div>
    </div>
  );

  const leftContent = viewMap[selectedPeriod as View] ?? null;

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t('ai_summary')}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  if (isLoading) return <Skeleton height="400px" />;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="top_products_detail"
    />
  );
};

export default TopProducts;
